/**
 * Turns an OAuth scope string into the plain-language lines shown on the
 * consent screen.
 *
 * This used to be a hardcoded list that always claimed create and edit access.
 * That is wrong for any read-only client: the Framer plugin requests
 * `admin:read` alone, so the screen was telling people they were granting write
 * access to their pages, payments and customers when they were not.
 */

const FAMILIES = [
  { resources: ['pages', 'products'], noun: 'your checkout pages, events, and forms' },
  {
    resources: ['payments', 'bookings', 'subscriptions', 'invoices'],
    noun: 'payments, bookings and subscriptions',
  },
  { resources: ['customers', 'submissions'], noun: 'customer data' },
  { resources: ['tickets'], noun: 'tickets' },
  { resources: ['coupons'], noun: 'coupons' },
  { resources: ['webhooks'], noun: 'webhooks' },
  { resources: ['account'], noun: 'your store name and logo' },
];

// Only the first three families are summarised for admin:*, so `account` stays last.
const ADMIN_NOUNS = FAMILIES.slice(0, 3).map(family => family.noun);

function parseScopes(scope) {
  return String(scope || '')
    .split(/\s+/)
    .filter(Boolean)
    .map(entry => entry.toLowerCase());
}

function line(noun, canWrite) {
  return `${canWrite ? 'View and manage' : 'View'} ${noun}`;
}

export function describeScopes(scope) {
  const scopes = parseScopes(scope);

  if (scopes.includes('admin:write')) {
    return ADMIN_NOUNS.map(noun => line(noun, true));
  }

  if (scopes.includes('admin:read')) {
    return ADMIN_NOUNS.map(noun => line(noun, false));
  }

  const lines = FAMILIES.reduce((acc, family) => {
    const canWrite = family.resources.some(resource => scopes.includes(`${resource}:write`));
    const canRead = family.resources.some(resource => scopes.includes(`${resource}:read`));
    if (canWrite || canRead) acc.push(line(family.noun, canWrite));
    return acc;
  }, []);

  // An unrecognised or absent scope must not silently render an empty list, and
  // must not overstate. Say only what we can stand behind.
  return lines.length ? lines : ['Access your Checkout Page account'];
}

export default describeScopes;
